import { c as _c } from "react/compiler-runtime";
import { IcoArrowDown16, IcoArrowUp16, IcoPlusSmall16, IcoTrash16, IcoWarning16 } from '@onefootprint/icons';
import type { LanguageDefinitionResponse, NodeExecutionDetailRules, PublicTraceEntry, TemplateAutocomplete } from '@onefootprint/request-types/dashboard';
import { Form, IconButton, LinkButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ExpressionEditor } from '@/components/expression-editor';
import { getTraceHighlightStyles } from '@/components/playbook-builder/utils/get-trace-highlight-styles';
import CustomBadge from '../../conditional-node/components/custom-badge';
import type { RulesFormData } from '../rules-node.types';
type RulesGroupProps = {
  groupIndex: number;
  canEdit: boolean;
  autocompleteItems: TemplateAutocomplete;
  languageDefinition?: LanguageDefinitionResponse;
  onRemoveGroup: () => void;
  numGroups: number;
  onMoveGroup: (oldIndex: number, newIndex: number) => void;
  trace?: PublicTraceEntry;
  warningMessages?: Map<string, string>;
};
export const newCondition = () => {
  const generateId = () => Math.random().toString(36).substring(2) + Date.now().toString(36);
  return {
    expression: '',
    id: `rule_${generateId()}`
  };
};
export const RulesGroup = t0 => {
  const $ = _c(103);
  const {
    groupIndex,
    canEdit,
    autocompleteItems,
    languageDefinition,
    onRemoveGroup,
    numGroups,
    onMoveGroup,
    trace,
    warningMessages
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "rulesDataNode"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "rules"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    t: tRules
  } = useTranslation("playbook-builder", t2);
  const {
    control
  } = useFormContext();
  const t3 = `groups.${groupIndex}.value`;
  let t4;
  if ($[2] !== control || $[3] !== t3) {
    t4 = {
      control,
      name: t3
    };
    $[2] = control;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const groupValue = useWatch(t4);
  const t5 = `groups.${groupIndex}.conditions`;
  let t6;
  if ($[5] !== control || $[6] !== t5) {
    t6 = {
      control,
      name: t5,
      keyName: "_fieldId"
    };
    $[5] = control;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const {
    fields: groupConditions,
    append: appendCondition,
    remove: removeCondition
  } = useFieldArray(t6);
  const nodeExecDetail = trace?.context as NodeExecutionDetailRules | undefined;
  const wasGroupExecuted = nodeExecDetail?.kind === "rules" && nodeExecDetail?.assignedValue === groupValue;
  let handleMoveGroupDown;
  let handleMoveGroupUp;
  let hasNextGroup;
  let hasPreviousGroup;
  let isOnlyGroup;
  let onAddCondition;
  let t7;
  let wasConditionTriggered;
  if ($[8] !== appendCondition || $[9] !== groupIndex || $[10] !== nodeExecDetail || $[11] !== numGroups || $[12] !== onMoveGroup || $[13] !== wasGroupExecuted) {
    const borderClass = getTraceHighlightStyles(wasGroupExecuted);
    let t8;
    if ($[22] !== appendCondition) {
      t8 = () => {
        appendCondition(newCondition());
      };
      $[22] = appendCondition;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    onAddCondition = t8;
    isOnlyGroup = numGroups === 1;
    hasNextGroup = groupIndex < numGroups - 1;
    hasPreviousGroup = groupIndex > 0;
    let t9;
    if ($[24] !== groupIndex || $[25] !== onMoveGroup) {
      t9 = () => onMoveGroup(groupIndex, groupIndex - 1);
      $[24] = groupIndex;
      $[25] = onMoveGroup;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    handleMoveGroupUp = t9;
    let t10;
    if ($[27] !== groupIndex || $[28] !== onMoveGroup) {
      t10 = () => onMoveGroup(groupIndex, groupIndex + 1);
      $[27] = groupIndex;
      $[28] = onMoveGroup;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    handleMoveGroupDown = t10;
    let t11;
    if ($[30] !== nodeExecDetail || $[31] !== wasGroupExecuted) {
      t11 = conditionId => {
        if (!nodeExecDetail || !wasGroupExecuted) {
          return false;
        }
        return nodeExecDetail.groupResults.find(r => r.ruleId === conditionId)?.result === true;
      };
      $[30] = nodeExecDetail;
      $[31] = wasGroupExecuted;
      $[32] = t11;
    } else {
      t11 = $[32];
    }
    wasConditionTriggered = t11;
    t7 = cx("rounded flex flex-col gap-3", borderClass);
    $[8] = appendCondition;
    $[9] = groupIndex;
    $[10] = nodeExecDetail;
    $[11] = numGroups;
    $[12] = onMoveGroup;
    $[13] = wasGroupExecuted;
    $[14] = handleMoveGroupDown;
    $[15] = handleMoveGroupUp;
    $[16] = hasNextGroup;
    $[17] = hasPreviousGroup;
    $[18] = isOnlyGroup;
    $[19] = onAddCondition;
    $[20] = t7;
    $[21] = wasConditionTriggered;
  } else {
    handleMoveGroupDown = $[14];
    handleMoveGroupUp = $[15];
    hasNextGroup = $[16];
    hasPreviousGroup = $[17];
    isOnlyGroup = $[18];
    onAddCondition = $[19];
    t7 = $[20];
    wasConditionTriggered = $[21];
  }
  let t8;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="text-body-2 text-tertiary flex flex-row gap-1 whitespace-nowrap">Set value to</span>;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  const t9 = `groups.${groupIndex}.value`;
  let t10;
  if ($[34] !== t) {
    t10 = t(_temp);
    $[34] = t;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== t) {
    t11 = t(_temp2);
    $[36] = t;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== t11) {
    t12 = {
      required: t11
    };
    $[38] = t11;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== t10 || $[41] !== t12 || $[42] !== t9) {
    t13 = <div className="flex flex-row items-center gap-2 w-full min-h-[32px]">{t8}<Form.Input name={t9} placeholder={t10} size="compact" rules={t12} className="max-w-[200px]" /></div>;
    $[40] = t10;
    $[41] = t12;
    $[42] = t9;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== canEdit || $[45] !== handleMoveGroupUp || $[46] !== hasPreviousGroup || $[47] !== t) {
    t14 = hasPreviousGroup && <IconButton aria-label={t(_temp3)} onClick={handleMoveGroupUp} icon={IcoArrowUp16} size="tiny" disabled={!canEdit} />;
    $[44] = canEdit;
    $[45] = handleMoveGroupUp;
    $[46] = hasPreviousGroup;
    $[47] = t;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[49] !== canEdit || $[50] !== handleMoveGroupDown || $[51] !== hasNextGroup || $[52] !== t) {
    t15 = hasNextGroup && <IconButton aria-label={t(_temp4)} onClick={handleMoveGroupDown} icon={IcoArrowDown16} size="tiny" disabled={!canEdit} />;
    $[49] = canEdit;
    $[50] = handleMoveGroupDown;
    $[51] = hasNextGroup;
    $[52] = t;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  let t16;
  if ($[54] !== canEdit || $[55] !== isOnlyGroup || $[56] !== onRemoveGroup || $[57] !== t) {
    t16 = !isOnlyGroup && <IconButton aria-label={t(_temp5)} onClick={onRemoveGroup} icon={IcoTrash16} size="tiny" disabled={!canEdit} destructive={true} />;
    $[54] = canEdit;
    $[55] = isOnlyGroup;
    $[56] = onRemoveGroup;
    $[57] = t;
    $[58] = t16;
  } else {
    t16 = $[58];
  }
  let t17;
  if ($[59] !== t13 || $[60] !== t14 || $[61] !== t15 || $[62] !== t16) {
    t17 = <div className="flex flex-row bg-secondary border-b border-b-tertiary p-2 items-center gap-3">{t13}{t14}{t15}{t16}</div>;
    $[59] = t13;
    $[60] = t14;
    $[61] = t15;
    $[62] = t16;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  let t18;
  if ($[64] !== autocompleteItems || $[65] !== canEdit || $[66] !== control || $[67] !== groupConditions || $[68] !== groupIndex || $[69] !== languageDefinition || $[70] !== removeCondition || $[71] !== t || $[72] !== tRules || $[73] !== warningMessages || $[74] !== wasConditionTriggered) {
    let t19;
    if ($[76] !== autocompleteItems || $[77] !== canEdit || $[78] !== control || $[79] !== groupConditions.length || $[80] !== groupIndex || $[81] !== languageDefinition || $[82] !== removeCondition || $[83] !== t || $[84] !== tRules || $[85] !== warningMessages || $[86] !== wasConditionTriggered) {
      t19 = (condition, conditionIndex) => {
        const wasTriggered = wasConditionTriggered(condition.id);
        const warningMessage = warningMessages?.get(condition.id);
        return <div key={condition._fieldId} className="flex items-start gap-2"><span className="text-caption-1 text-tertiary min-w-[20px] pt-2">{conditionIndex === 0 ? t(_temp6) : t(_temp7)}</span><Controller name={`groups.${groupIndex}.conditions.${conditionIndex}.expression`} control={control} rules={{
            required: t(_temp8)
          }} render={t20 => {
            const {
              field,
              fieldState
            } = t20;
            return <div className="flex flex-col w-full relative"><div className="relative"><ExpressionEditor {...field} variant="input" hasError={!!fieldState.error} autocompleteItems={autocompleteItems} languageDefinition={languageDefinition} disabled={!canEdit || field.disabled} placeholder={t(_temp9)} className={wasTriggered ? "border-accent! border-2!" : undefined} />{wasTriggered && <CustomBadge>{tRules(_temp10)}</CustomBadge>}</div>{fieldState.error?.message && <span className="text-error text-caption-2 mt-2">{fieldState.error.message}</span>}{warningMessage && !fieldState.error?.message && <span className="text-warning text-caption-2 mt-2 flex items-center gap-1.5"><IcoWarning16 color="warning" className="shrink-0 size-3" />{warningMessage}</span>}</div>;
          }} /><IconButton type="button" onClick={() => removeCondition(conditionIndex)} icon={IcoTrash16} disabled={groupConditions.length === 1 || !canEdit} destructive={true} aria-label={t(_temp11)} /></div>;
      };
      $[76] = autocompleteItems;
      $[77] = canEdit;
      $[78] = control;
      $[79] = groupConditions.length;
      $[80] = groupIndex;
      $[81] = languageDefinition;
      $[82] = removeCondition;
      $[83] = t;
      $[84] = tRules;
      $[85] = warningMessages;
      $[86] = wasConditionTriggered;
      $[87] = t19;
    } else {
      t19 = $[87];
    }
    t18 = groupConditions.map(t19);
    $[64] = autocompleteItems;
    $[65] = canEdit;
    $[66] = control;
    $[67] = groupConditions;
    $[68] = groupIndex;
    $[69] = languageDefinition;
    $[70] = removeCondition;
    $[71] = t;
    $[72] = tRules;
    $[73] = warningMessages;
    $[74] = wasConditionTriggered;
    $[75] = t18;
  } else {
    t18 = $[75];
  }
  let t19;
  if ($[88] !== t18) {
    t19 = <div className="flex flex-col gap-2">{t18}</div>;
    $[88] = t18;
    $[89] = t19;
  } else {
    t19 = $[89];
  }
  const t20 = !canEdit;
  let t21;
  if ($[90] !== t) {
    t21 = t(_temp12);
    $[90] = t;
    $[91] = t21;
  } else {
    t21 = $[91];
  }
  let t22;
  if ($[92] !== onAddCondition || $[93] !== t20 || $[94] !== t21) {
    t22 = <LinkButton type="button" onClick={onAddCondition} iconComponent={IcoPlusSmall16} iconPosition="left" disabled={t20}>{t21}</LinkButton>;
    $[92] = onAddCondition;
    $[93] = t20;
    $[94] = t21;
    $[95] = t22;
  } else {
    t22 = $[95];
  }
  let t23;
  if ($[96] !== t19 || $[97] !== t22) {
    t23 = <div className="flex flex-col gap-3 px-3 pb-3">{t19}{t22}</div>;
    $[96] = t19;
    $[97] = t22;
    $[98] = t23;
  } else {
    t23 = $[98];
  }
  let t24;
  if ($[99] !== t17 || $[100] !== t23 || $[101] !== t7) {
    t24 = <div className={t7}>{t17}{t23}</div>;
    $[99] = t17;
    $[100] = t23;
    $[101] = t7;
    $[102] = t24;
  } else {
    t24 = $[102];
  }
  return t24;
};
function _temp($) {
  return $.valuePlaceholder;
}
function _temp2($_0) {
  return $_0.valueRequired;
}
function _temp3($_1) {
  return $_1.moveGroupUp;
}
function _temp4($_2) {
  return $_2.moveGroupDown;
}
function _temp5($_3) {
  return $_3.deleteGroup;
}
function _temp6($_4) {
  return $_4.if;
}
function _temp7($_5) {
  return $_5.or;
}
function _temp8($_6) {
  return $_6.conditionRequired;
}
function _temp9($_7) {
  return $_7.conditionPlaceholder;
}
function _temp10($_8) {
  return $_8.triggered;
}
function _temp11($_9) {
  return $_9.removeCondition;
}
function _temp12($_10) {
  return $_10.addCondition;
}